import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "./scrollTo";
import { Container } from "./Container";

const NAV = [
  { id: "hero", label: "Начало" },
  { id: "about", label: "Кои сме ние" },
  { id: "prices", label: "Цени" },
  { id: "clients", label: "Клиенти" },
  { id: "contact", label: "Контакти" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.6);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // while the overlay menu is open the page behind must not scroll
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      window.__lenis?.stop();
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      window.__lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  const go = (id) => (e) => {
    setOpen(false);
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
    window.__lenis?.start();
    scrollToId(id)(e);
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "bg-[#15181C]/80 backdrop-blur-md border-b border-[rgba(255,255,255,.14)]"
            : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <Container className="header-bar flex items-center justify-between">
          <a
            href="#hero"
            onClick={go("hero")}
            className="flex items-center"
            data-testid="header-logo"
          >
            <Logo height={62} />
          </a>

          {/* desktop navigation — 1024px and up */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={go(n.id)}
                className="text-[15px] font-normal uppercase tracking-[0.12em] hover:text-[#EE443E] transition-colors"
                data-testid={`header-link-${n.id}`}
              >
                {n.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={go("contact")}
              className="rounded-full bg-[#EE443E] text-white px-6 text-sm font-semibold uppercase tracking-wide hover:opacity-90 transition-opacity"
              data-testid="header-cta"
            >
              Свържи се
            </a>
          </nav>

          {/* tablet + phone — pill CTA on tablet only, hamburger below 1024px */}
          <div className="lg:hidden flex items-center gap-3">
            <a
              href="#contact"
              onClick={go("contact")}
              className="header-cta-mid rounded-full bg-[#EE443E] text-white px-6 text-sm font-semibold uppercase tracking-wide hover:opacity-90 transition-opacity"
              data-testid="header-cta-mid"
            >
              Свържи се
            </a>
            <button
              onClick={() => setOpen(true)}
              aria-label="Отвори меню"
              className="w-12 h-12 flex flex-col items-center justify-center"
              data-testid="header-menu-toggle"
            >
              <span className="block w-6 h-[2px] bg-white" />
              <span className="block w-6 h-[2px] bg-white mt-[5px]" />
            </button>
          </div>
        </Container>
      </header>

      {/* full-screen menu overlay — below 1024px */}
      {open && (
        <div
          className="fixed inset-0 z-[100] bg-[#15181C] lg:hidden flex flex-col items-center justify-center"
          data-testid="header-menu-overlay"
        >
          <div className="absolute top-0 inset-x-0">
            <Container className="header-bar flex items-center justify-end">
              <button
                onClick={() => setOpen(false)}
                aria-label="Затвори меню"
                className="w-12 h-12 flex items-center justify-center text-white"
                data-testid="header-menu-close"
              >
                <X className="w-6 h-6" />
              </button>
            </Container>
          </div>

          <nav className="flex flex-col items-center gap-[28px]">
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={go(n.id)}
                className="font-display text-[28px] text-white inline-flex items-center min-h-[48px]"
                data-testid={`menu-link-${n.id}`}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <a
            href="#contact"
            onClick={go("contact")}
            className="mt-[40px] inline-flex items-center rounded-full bg-[#EE443E] text-white px-8 min-h-[48px] text-sm font-semibold uppercase tracking-wide hover:opacity-90 transition-opacity"
            data-testid="menu-cta"
          >
            Свържи се
          </a>
        </div>
      )}
    </>
  );
};

export default Header;
